package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.KioskException
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskSession
import com.evoapps.evoshift.domain.model.KioskTipPool
import com.evoapps.evoshift.domain.model.TipSplit
import com.evoapps.evoshift.domain.repository.KioskRepository
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.tips_amount_invalid
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** Where the tablet's tips are: closed, a manager to pick, their PIN, the amount, the split. */
sealed interface KioskTipsStep {
    data object Closed : KioskTipsStep

    data object PickManager : KioskTipsStep

    data class Pin(val manager: KioskPerson, val digits: String = "") : KioskTipsStep

    /**
     * The day's amount; [pool] is the day as the server has it (amountMinor 0: nothing entered
     * yet). The PIN stays here only until the amount is sent: the server checks it again.
     */
    data class Amount(val manager: KioskPerson, val pin: String, val pool: KioskTipPool, val text: String = "") : KioskTipsStep

    /** The split, shown to the manager who entered it. */
    data class Done(val manager: KioskPerson, val pin: String, val pool: KioskTipPool) : KioskTipsStep
}

data class KioskTipsUiState(
    val step: KioskTipsStep = KioskTipsStep.Closed,
    val isSending: Boolean = false,
    val error: UiText? = null
) {
    val isOpen: Boolean get() = step != KioskTipsStep.Closed
}

/**
 * SH-124: the day's tips on the clock-in tablet. A manager picks themselves, confirms with their
 * own PIN (kioskTipPool checks it on the server, with the lockout of clocking), enters the day's
 * amount and sees the split. Nothing of it stays on the tablet: closing, or [IDLE_MILLIS] without
 * a touch, forgets the PIN and the split. People see their own share after clocking out
 * (KioskClockResult.tipShareMinor), never here.
 */
class KioskTipsViewModel(
    private val session: KioskSession,
    private val repository: KioskRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(KioskTipsUiState())
    val uiState: StateFlow<KioskTipsUiState> = _uiState.asStateFlow()
    private var idleJob: Job? = null

    /** "Tips" on the tablet's grid. */
    fun open() = go(KioskTipsStep.PickManager)

    fun close() {
        idleJob?.cancel()
        _uiState.value = KioskTipsUiState()
    }

    fun pickManager(manager: KioskPerson) {
        if (!manager.isManager) return
        go(KioskTipsStep.Pin(manager))
    }

    fun digit(d: Char) {
        if (!d.isDigit()) return
        val step = _uiState.value.step as? KioskTipsStep.Pin ?: return
        if (step.digits.length < KioskViewModel.PIN_LENGTH) go(step.copy(digits = step.digits + d))
    }

    fun backspace() {
        val step = _uiState.value.step as? KioskTipsStep.Pin ?: return
        go(step.copy(digits = step.digits.dropLast(1)))
    }

    /** The PIN: the server checks it and returns the day's pool so far. */
    fun submitPin() {
        val step = _uiState.value.step as? KioskTipsStep.Pin ?: return
        if (step.digits.length != KioskViewModel.PIN_LENGTH || _uiState.value.isSending) return
        _uiState.update { it.copy(isSending = true, error = null) }
        viewModelScope.launch {
            repository.openTips(step.manager.userId, step.digits).fold(
                onSuccess = { pool ->
                    val text = pool.amountMinor.takeIf { it > 0 }?.let { TipsViewModel.formatAmountInput(it) }.orEmpty()
                    _uiState.update { it.copy(isSending = false) }
                    go(KioskTipsStep.Amount(step.manager, step.digits, pool, text))
                },
                onFailure = { e -> fail(e, step.copy(digits = "")) }
            )
        }
    }

    fun setAmount(text: String) {
        val step = _uiState.value.step as? KioskTipsStep.Amount ?: return
        go(step.copy(text = text.filter { it.isDigit() || it == ',' || it == '.' }.take(10)))
    }

    /** "Split the tips": the server stores the day's pool and splits it by the hours clocked. */
    fun submitAmount() {
        val step = _uiState.value.step as? KioskTipsStep.Amount ?: return
        if (_uiState.value.isSending) return
        val amount = TipSplit.parseAmount(step.text)
        if (amount == null) {
            _uiState.update { it.copy(error = UiText.Resource(Res.string.tips_amount_invalid)) }
            touched()
            return
        }
        _uiState.update { it.copy(isSending = true, error = null) }
        viewModelScope.launch {
            repository.saveTips(step.manager.userId, step.pin, amount).fold(
                onSuccess = { pool ->
                    _uiState.update { it.copy(isSending = false) }
                    go(KioskTipsStep.Done(step.manager, step.pin, pool))
                },
                onFailure = { e -> fail(e, step) }
            )
        }
    }

    /** From the split back to the amount. */
    fun changeAmount() {
        val step = _uiState.value.step as? KioskTipsStep.Done ?: return
        go(KioskTipsStep.Amount(step.manager, step.pin, step.pool, TipsViewModel.formatAmountInput(step.pool.amountMinor)))
    }

    private fun fail(e: Throwable, stay: KioskTipsStep) {
        // A locked or unpaired tablet goes back to the start; a wrong PIN tries again.
        val reason = (e as? KioskException)?.reason
        val next = if (reason == KioskException.Reason.LOCKED || reason == KioskException.Reason.REVOKED) KioskTipsStep.PickManager else stay
        _uiState.update { it.copy(isSending = false, error = e.toUiText(), step = next) }
        touched()
    }

    private fun go(step: KioskTipsStep) {
        _uiState.update { it.copy(step = step, error = null) }
        touched()
    }

    /** Any touch restarts the inactivity timer; when it runs out, everything is forgotten. */
    private fun touched() {
        idleJob?.cancel()
        idleJob = viewModelScope.launch {
            delay(IDLE_MILLIS)
            close()
        }
    }

    /** The workplace the tablet belongs to (the tips are always its own). */
    val workplaceName: String get() = session.workplaceName

    companion object {
        const val IDLE_MILLIS = 60_000L
    }
}
